<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue'
import Icon from './Icon.vue'
import { useI18n } from '../composables/useI18n'

const { locale, setLocale, t } = useI18n()

const mobileOpen = ref(false)
const menuButtonRef = ref<HTMLButtonElement | null>(null)
const menuPanelRef = ref<HTMLUListElement | null>(null)

const links = computed(() => [
  { label: t('nav.painPoints'), href: '#pain-points' },
  { label: t('nav.what'), href: '#what' },
  { label: t('nav.how'), href: '#how' },
  { label: t('nav.components'), href: '#components' },
  { label: t('nav.getStarted'), href: '#get-started' },
  { label: t('nav.download'), href: '#download' },
])

function toggleLocale() {
  setLocale(locale.value === 'zh' ? 'en' : 'zh')
}

function closeMobileMenu() {
  mobileOpen.value = false
}

function handleEscape(e: KeyboardEvent) {
  if (e.key === 'Escape' && mobileOpen.value) {
    closeMobileMenu()
  }
}

function handleClickOutside(e: MouseEvent) {
  const target = e.target as Node
  if (
    mobileOpen.value &&
    menuPanelRef.value &&
    menuButtonRef.value &&
    !menuPanelRef.value.contains(target) &&
    !menuButtonRef.value.contains(target)
  ) {
    closeMobileMenu()
  }
}

let cleanup = () => {}

watch(mobileOpen, (open) => {
  if (open) {
    document.addEventListener('keydown', handleEscape)
    document.addEventListener('click', handleClickOutside)
    cleanup = () => {
      document.removeEventListener('keydown', handleEscape)
      document.removeEventListener('click', handleClickOutside)
    }
  } else {
    cleanup()
    cleanup = () => {}
  }
})

onUnmounted(() => {
  cleanup()
})
</script>

<template>
  <header class="sticky top-0 z-50 bg-base-100/80 backdrop-blur border-b border-base-300">
    <nav class="navbar max-w-6xl mx-auto px-4">
      <div class="navbar-start">
        <a href="#" class="flex items-center gap-2 text-primary font-bold text-lg">
          <img src="/logo.png" alt="OpenAaaS" class="h-8 w-auto" />
          <span>OpenAaaS</span>
        </a>
      </div>

      <div class="navbar-center hidden md:flex">
        <ul class="menu menu-horizontal gap-1 text-sm">
          <li v-for="link in links" :key="link.href">
            <a :href="link.href" class="font-medium">{{ link.label }}</a>
          </li>
        </ul>
      </div>

      <div class="navbar-end gap-2">
        <button
          class="btn btn-ghost btn-sm"
          :aria-label="locale === 'zh' ? 'Switch to English' : '切换到中文'"
          @click="toggleLocale"
        >
          {{ t('nav.toggleLang') }}
        </button>
        <a
          href="https://github.com/Wolido/OpenAaaS"
          target="_blank"
          rel="noopener noreferrer"
          class="btn btn-ghost btn-sm hidden sm:inline-flex"
        >
          <Icon name="github" iconClass="w-4 h-4" />
          {{ t('nav.github') }}
        </a>
        <div class="relative md:hidden">
          <button
            ref="menuButtonRef"
            class="btn btn-ghost btn-sm btn-square"
            :aria-label="t('nav.menu')"
            aria-controls="mobile-menu"
            :aria-expanded="mobileOpen"
            @click="mobileOpen = !mobileOpen"
          >
            <Icon name="menu" iconClass="w-5 h-5" />
          </button>
          <ul
            v-show="mobileOpen"
            id="mobile-menu"
            ref="menuPanelRef"
            class="menu absolute right-0 top-full mt-2 w-40 rounded-box bg-base-100 shadow-md p-2 z-50"
          >
            <li v-for="link in links" :key="link.href">
              <a :href="link.href" @click="mobileOpen = false">{{ link.label }}</a>
            </li>
            <li class="border-t border-base-300 mt-2 pt-2">
              <button class="w-full text-left" @click="toggleLocale(); mobileOpen = false">
                {{ t('nav.toggleLang') }}
              </button>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  </header>
</template>
